import { useCallback, useEffect, useRef, useState } from 'react'

import { shuffleArray, randomInRange } from '~/bootcamp/Activities/utils/activities.utils'

const NUMBER_SET_BY_DIFF_LEVELS: { [key: number]: { primeNumbers: number[]; rangeMin: number; rangeMax: number } } = {
  1: {
    primeNumbers: [2, 3, 5],
    rangeMin: 2,
    rangeMax: 10,
  },
  2: {
    primeNumbers: [5, 7, 11, 13, 17, 19],
    rangeMin: 10,
    rangeMax: 40,
  },
  3: {
    primeNumbers: [7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47],
    rangeMin: 40,
    rangeMax: 99,
  },
}

const generateQuestion = (
  diffLevel: number,
): {
  factorPrimeNumber: number
  options: number[]
  answer: number
} => {
  const { primeNumbers, rangeMin, rangeMax } = NUMBER_SET_BY_DIFF_LEVELS[diffLevel]
  const primeForQuestion = primeNumbers[randomInRange(0, primeNumbers.length)]

  let questionOptions: number[] = []
  let answerOption = 0

  const generateAnswerOption = (): void => {
    const option = primeForQuestion * randomInRange(2, 5)

    if (option <= rangeMax && option !== primeForQuestion) {
      answerOption = option
      questionOptions = [option]
      return
    } else {
      generateAnswerOption()
    }
  }

  const generateCompoundNumbers = (): void => {
    const randomCompoundNumber = randomInRange(rangeMin, rangeMax)

    if (
      randomCompoundNumber !== primeForQuestion &&
      randomCompoundNumber % primeForQuestion !== 0 &&
      !questionOptions.includes(randomCompoundNumber)
    ) {
      questionOptions = [...questionOptions, randomCompoundNumber]
    }

    if (questionOptions.length < 3) {
      generateCompoundNumbers()
    }
  }

  generateAnswerOption()
  generateCompoundNumbers()

  return {
    factorPrimeNumber: primeForQuestion,
    options: shuffleArray(questionOptions),
    answer: answerOption,
  }
}

const useFactorSelect = (
  difficultyLevel = 1,
  taskHistory: number[],
): [number, number[], (selectedOption: number) => boolean] => {
  const [factorPrimeNumber, setFactorPrimeNumber] = useState<number>(0)
  const [options, setOptions] = useState<number[]>([])
  const [answer, setAnswer] = useState<number>(0)
  const retry = useRef<number>(0)

  const generateNewQuestion = useCallback((): void => {
    const newQuestionSet = generateQuestion(difficultyLevel)

    if (!taskHistory.includes(newQuestionSet.factorPrimeNumber) || retry.current >= 3) {
      retry.current = 0

      setFactorPrimeNumber(newQuestionSet.factorPrimeNumber)
      setOptions(newQuestionSet.options)
      setAnswer(newQuestionSet.answer)
    } else {
      retry.current += 1

      generateNewQuestion()
    }
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    generateNewQuestion()
  }, [generateNewQuestion])

  const checkAnswer = (selectedOption: number): boolean => {
    return selectedOption === answer
  }

  return [factorPrimeNumber, options, checkAnswer]
}

export default useFactorSelect
